import React, { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import {
  supplierGetMySubscriptions,
  supplierGetSubscriptionPlans,
  supplierRequestSubscriptionPlan,
} from "../../api/subscriptionApi";
import "../css/FornitoreAbbonamenti.css";

const euro = (cents) => {
  const value = Number(cents);
  if (!Number.isFinite(value)) return "—";

  return (value / 100).toLocaleString("it-IT", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const modelLabels = {
  discount: "Sconto",
  credits: "Crediti",
  recurring: "Ricorrente",
};

function formatDate(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleDateString("it-IT");
}

const statusLabels = {
  active: "Attivo",
  cancel_at_period_end: "Rinnovo disattivato",
  canceled: "Cancellato",
  paused: "In pausa",
  pending: "In attesa",
  approved: "Approvato",
  rejected: "Rifiutato",
};

const shortDayLabels = {
  0: "Dom",
  1: "Lun",
  2: "Mar",
  3: "Mer",
  4: "Gio",
  5: "Ven",
  6: "Sab",
  7: "Dom",
};

const configKeyLabels = {
  frequency: "Frequenza",
  max_items: "Numero massimo capi",
  preferred_days: "Giorni disponibili",
  discount_percent: "Sconto",
  free_delivery_per_month: "Consegne gratuite al mese",
  monthly_credits: "Credito mensile",
  rollover_percent: "Rollover credito",
};

const configValueLabels = {
  weekly: "Settimanale",
  biweekly: "Ogni 2 settimane",
  monthly: "Mensile",
  true: "Sì",
  false: "No",
};

function formatPreferredDays(value) {
  const days = Array.isArray(value) ? value : [];

  const normalized = [
    ...new Set(
      days
        .map((day) => {
          const n = Number(day);
          if (!Number.isInteger(n)) return null;
          if (n === 7) return 0;
          return n >= 0 && n <= 6 ? n : null;
        })
        .filter((day) => day !== null),
    ),
  ].sort((a, b) => a - b);

  if (!normalized.length) return "Non indicati";
  if (normalized.length === 7) return "Tutti";

  return normalized.map((day) => shortDayLabels[day]).join(", ");
}

function formatConfigValue(key, value) {
  if (key === "preferred_days") return formatPreferredDays(value);

  if (key === "frequency") {
    return configValueLabels[String(value)] || String(value || "—");
  }

  if (key === "monthly_credits") {
    return euro(value);
  }

  if (key === "discount_percent" || key === "rollover_percent") {
    const n = Number(value);
    return Number.isFinite(n) ? `${n}%` : "—";
  }

  if (typeof value === "boolean") {
    return configValueLabels[String(value)] || String(value);
  }

  if (value === null || value === undefined || value === "") return "—";

  if (typeof value === "object") {
    return JSON.stringify(value);
  }

  return configValueLabels[String(value)] || String(value);
}

function renderConfig(config) {
  if (!config || typeof config !== "object") return null;

  const entries = Object.entries(config).filter(
    ([key]) => key !== "presentation",
  );
  if (!entries.length) return null;

  return (
    <ul className="forn-sub-config">
      {entries.map(([key, value]) => (
        <li key={key}>
          <span>{configKeyLabels[key] || key}</span>
          <strong>{formatConfigValue(key, value)}</strong>
        </li>
      ))}
    </ul>
  );
}

export default function FornitoreAbbonamenti() {
  const token = localStorage.getItem("token");

  const [plans, setPlans] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [supplier, setSupplier] = useState(null);
  const [loading, setLoading] = useState(true);
  const [requestingId, setRequestingId] = useState(null);

  const approvedCount = useMemo(
    () => plans.filter((p) => p.supplier_status === "approved").length,
    [plans],
  );

  const pendingCount = useMemo(
    () => plans.filter((p) => p.supplier_status === "pending").length,
    [plans],
  );

  async function loadAll() {
    if (!token) return;

    setLoading(true);
    try {
      const [plansRes, subsRes] = await Promise.all([
        supplierGetSubscriptionPlans(token),
        supplierGetMySubscriptions(token),
      ]);

      setSupplier(plansRes?.supplier || subsRes?.supplier || null);
      setPlans(plansRes?.plans || []);
      setSubscriptions(subsRes?.subscriptions || []);
    } catch (err) {
      toast.error(err.message || "Errore caricamento abbonamenti");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleRequest(plan) {
    if (!token || !plan?.id) return;

    setRequestingId(plan.id);
    try {
      await supplierRequestSubscriptionPlan(
        {
          plan_id: plan.id,
          custom_overrides: {},
        },
        token,
      );

      toast.success("Richiesta adesione inviata");
      await loadAll();
    } catch (err) {
      toast.error(err.message || "Errore invio richiesta");
    } finally {
      setRequestingId(null);
    }
  }

  function getPlanAction(plan) {
    const status = plan.supplier_status;

    if (status === "approved") {
      return <span className="forn-sub-muted">Già approvato</span>;
    }

    if (status === "pending") {
      return <span className="forn-sub-muted">Richiesta già inviata</span>;
    }

    return (
      <button
        type="button"
        className="forn-sub-btn forn-sub-btn-primary"
        disabled={requestingId === plan.id}
        onClick={() => handleRequest(plan)}
      >
        {requestingId === plan.id ? "Invio..." : "Richiedi adesione"}
      </button>
    );
  }

  return (
    <div className="forn-sub-page">
      <section className="forn-sub-hero">
        <div>
          <p className="forn-sub-kicker">Area fornitore</p>
          <h1>Abbonamenti</h1>
          <p>
            Visualizza i piani disponibili, richiedi l’adesione e controlla i
            clienti abbonati al tuo fornitore.
          </p>
        </div>

        <div className="forn-sub-stats">
          <div>
            <strong>{plans.length}</strong>
            <span>Piani disponibili</span>
          </div>
          <div>
            <strong>{approvedCount}</strong>
            <span>Approvati</span>
          </div>
          <div>
            <strong>{pendingCount}</strong>
            <span>In attesa</span>
          </div>
        </div>
      </section>

      {supplier ? (
        <div className="forn-sub-supplier">
          Fornitore:{" "}
          <strong>{supplier.nome || supplier.name || `#${supplier.id}`}</strong>
        </div>
      ) : null}

      {loading ? (
        <div className="forn-sub-card forn-sub-empty">Caricamento...</div>
      ) : (
        <>
          <section className="forn-sub-section">
            <div className="forn-sub-section-head">
              <div>
                <h2>Piani disponibili</h2>
                <p>
                  Richiedi l’adesione ai piani che vuoi rendere disponibili ai
                  clienti.
                </p>
              </div>
            </div>

            {plans.length === 0 ? (
              <div className="forn-sub-card forn-sub-empty">
                Nessun piano abbonamento disponibile.
              </div>
            ) : (
              <div className="forn-sub-grid">
                {plans.map((plan) => {
                  const status = plan.supplier_status;
                  const modelLabel =
                    modelLabels[plan.model_code] ||
                    plan.model_name ||
                    plan.model_code;

                  return (
                    <article className="forn-sub-plan-card" key={plan.id}>
                      <div className="forn-sub-img-wrap">
                        {plan.image_url ? (
                          <img src={plan.image_url} alt={plan.name} />
                        ) : (
                          <div className="forn-sub-img-placeholder">
                            Abbonamento
                          </div>
                        )}

                        {status ? (
                          <span
                            className={`forn-sub-badge forn-sub-badge-${status}`}
                          >
                            {statusLabels[status] || status}
                          </span>
                        ) : (
                          <span className="forn-sub-badge">Disponibile</span>
                        )}
                      </div>

                      <div className="forn-sub-plan-body">
                        <div className="forn-sub-plan-top">
                          <div>
                            <h3>{plan.name}</h3>
                            <span>{modelLabel}</span>
                          </div>
                          <strong>{euro(plan.price_cents)}</strong>
                        </div>

                        {plan.description ? (
                          <p className="forn-sub-desc">{plan.description}</p>
                        ) : (
                          <p className="forn-sub-desc muted">
                            Nessuna descrizione inserita.
                          </p>
                        )}

                        {renderConfig(plan.config)}

                        {plan.review_note ? (
                          <div className="forn-sub-note">
                            Nota revisione: {plan.review_note}
                          </div>
                        ) : null}

                        <div className="forn-sub-actions">
                          {getPlanAction(plan)}
                        </div>
                      </div>
                    </article>
                  );
                })}
              </div>
            )}
          </section>

          <section className="forn-sub-section">
            <div className="forn-sub-section-head">
              <div>
                <h2>Clienti abbonati</h2>
                <p>
                  Elenco delle sottoscrizioni cliente collegate al tuo
                  fornitore.
                </p>
              </div>
            </div>

            {subscriptions.length === 0 ? (
              <div className="forn-sub-card forn-sub-empty">
                Nessun cliente abbonato al momento.
              </div>
            ) : (
              <div className="forn-sub-table-wrap">
                <table className="forn-sub-table">
                  <thead>
                    <tr>
                      <th>Cliente</th>
                      <th>Email</th>
                      <th>Piano</th>
                      <th>Modello</th>
                      <th>Stato</th>
                      <th>Inizio</th>
                      <th>Fine periodo</th>
                      <th>Ritiro settimanale</th>
                      <th>Consegna settimanale</th>
                    </tr>
                  </thead>
                  <tbody>
                    {subscriptions.map((sub) => {
                      const fullName = [sub.user_first_name, sub.user_last_name]
                        .filter(Boolean)
                        .join(" ");

                      return (
                        <tr key={sub.id}>
                          <td>{fullName || `Utente #${sub.user_id}`}</td>
                          <td>{sub.user_email || "—"}</td>
                          <td>{sub.plan_name}</td>
                          <td>
                            {modelLabels[sub.model_code] || sub.model_name}
                          </td>
                          <td>
                            {statusLabels[sub.status] || sub.status || "—"}
                          </td>
                          <td>{formatDate(sub.start_date)}</td>
                          <td>{formatDate(sub.current_period_end)}</td>
                          {["pickup", "delivery"].map((kind) => (
                            <td key={kind}>{sub[kind + "_day_index"] != null && sub[kind + "_start"] && sub[kind + "_end"]
                              ? `${["Domenica", "Lunedì", "Martedì", "Mercoledì", "Giovedì", "Venerdì", "Sabato"][sub[kind + "_day_index"]]} ${String(sub[kind + "_start"]).slice(0, 5)}–${String(sub[kind + "_end"]).slice(0, 5)}`
                              : "Non programmato"}</td>
                          ))}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </>
      )}
    </div>
  );
}
